import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBookingsAvailabilityV2AvailabilityTimeSlotsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    _: [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_availability-time-slots';

/**
 * Retrieves a list of appointment time slots that match the provided filters.
 *
 *
 * ### Defaults
 *
 * List Availability Time Slots uses the following defaults:
 *
 * - Sorts time slots by `localStartDate` in ascending order.
 * - `cursorPaging.limit` set to `1000`.
 * - Returns both bookable and un-bookable time slots.
 * - If `bookable` filter isn't specified, bookable slots are returned first.
 * - `fromLocalDate` is set to the current time.
 *
 * ### Filtering behavior
 *
 * The method automatically filters time slots:
 *
 * - **Past time slots**: Only time slots starting from the specified `fromLocalDate` are included. When you omit `fromLocalDate`, Wix Bookings uses the current time as the default.
 * - **Resource availability**: Only time slots with at least some available resources are returned.
 *
 * ### Service type limitations
 *
 * Only appointment-based services are supported when calling List Availability Time Slots.
 *
 * To retrieve class availability, you can call [List Event Time Slots](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/list-event-time-slots).
 *
 * To retrieve course availability, you can follow the [End-to-End Booking Flow for course](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
 *
 * ### Booking policy filtering
 *
 * Use `bookingPolicyViolations` to filter slots based on booking restrictions defined in your [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).
 *
 * *Default behavior**: When you don't specify any `bookingPolicyViolations` filters, all slots are returned regardless of their booking policy status.
 *
 * - Set `tooEarlyToBook` to `true` to retrieve slots that can't be booked yet due to minimum advance booking time restrictions.
 * Currently, you can't specify `earliestBookingDate` to further filter by when the slot becomes bookable.
 * - Set `tooLateToBook` to `true` to retrieve slots that can no longer be booked due to maximum advance booking time restrictions.
 * - Set `bookOnlineDisabled` to `true` or `false` to filter by online booking availability settings.
 *
 * ### Available resources
 *
 * Time slots aren't returned when they have no available resources.
 *
 * Each time slot includes details for up to 10 available resources.
 * If there are more than 10 resources, you can call [Get Availability Time Slots](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/get-availability-time-slot) and filter by the resource Type ID to get details for the remaining resources.
 *
 * ### Customer choices
 *
 * Use `customerChoices` to calculate availability based on the customer's selected service duration or add-ons.
 *
 * When using [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction), we recommend passing only `addOnIds` in `customerChoices` and omitting `durationInMinutes`. Wix Bookings then calculates the total duration automatically. If you specify both `addOnIds` and `durationInMinutes`, `durationInMinutes` must equal the service duration plus all selected add-on durations.
 *
 * ### Business hours exception
 *
 * Wix Bookings disregards business opening hours when all of the following conditions are met:
 *
 * 1. 1 or more [staff members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction) are needed to provide the service.
 * 2. No other [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction) is needed to provide the service.
 * 3. The service doesn't have duration-based [variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).
 * 4. The service doesn't support [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction).
 *
 * In these cases, the working hours of the relevant staff member are used for availability calculation.
 */
export function listAvailabilityTimeSlots(
  payload: object
): RequestOptionsFactory<any> {
  function __listAvailabilityTimeSlots({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'bookingPolicyViolations.earliestBookingDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.availability.v2.time_slot',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.availability.v2.AvailabilityTimeSlots.ListAvailabilityTimeSlots',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAvailabilityV2AvailabilityTimeSlotsUrl({
        protoPath: '/v2/time-slots',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlots.bookingPolicyViolations.earliestBookingDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAvailabilityTimeSlots;
}

/**
 * Retrieves detailed information about a specific appointment time slot.
 *
 *
 * Call this method to get complete resource availability after finding a suitable slot with [List Availability Time Slots](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/list-availability-time-slots).
 *
 * ### Defaults
 *
 * Get Availability Time Slot uses the following defaults:
 *
 * - Returns all available resources unless filtered by `resourceIds` or `includeResourceTypeIds`.
 * - Includes full booking status and capacity details
 *
 * ### Service type limitations
 *
 * Only appointment-based services are supported when calling Get Availability Time Slot.
 *
 * To retrieve class session availability, you can call [Get Event Time Slot](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/get-event-time-slot).
 *
 * To retrieve course availability, you can follow the [End-to-End Booking Flow for courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
 *
 * ### Business hours exception
 *
 * Wix Bookings disregards business opening hours when all of the following conditions are met:
 *
 * 1. 1 or more [staff members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction) are needed to provide the service.
 * 2. No other [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction) is needed to provide the service.
 * 3. The service doesn't have duration-based [variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).
 * 4. The service doesn't support [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction).
 *
 * In these cases, the working hours of the relevant staff member are used instead for availability calculation.
 */
export function getAvailabilityTimeSlot(
  payload: object
): RequestOptionsFactory<any> {
  function __getAvailabilityTimeSlot({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.availability.v2.time_slot',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.availability.v2.AvailabilityTimeSlots.GetAvailabilityTimeSlot',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAvailabilityV2AvailabilityTimeSlotsUrl({
        protoPath: '/v2/time-slots/get',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlot.bookingPolicyViolations.earliestBookingDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAvailabilityTimeSlot;
}
